import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api.js';
import RichEditor from '../components/RichEditor.jsx';

function bytes(n) {
  if (n == null) return '-';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0) + ' ' + u[i];
}

export default function PostEditor({ kind }) {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const isNotice = kind === 'notices';
  const [title, setTitle] = useState('');
  const [html, setHtml] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState([]); // server-side attachments
  const [pending, setPending] = useState([]);   // File[] to upload on save
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editing) api.getPost(kind, id).then(p => { setTitle(p.title || ''); setHtml(p.content_html || ''); });
  }, [kind, id, editing]);

  useEffect(() => {
    if (editing && isNotice) {
      api.listNoticeAttachments(id).then(setExisting).catch(() => {});
    }
  }, [editing, isNotice, id]);

  const addPending = (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setPending(prev => [...prev, ...arr]);
  };
  const removePending = (idx) => setPending(prev => prev.filter((_, i) => i !== idx));
  const removeExisting = async (a) => {
    if (!window.confirm(`'${a.original_name}' 첨부파일을 삭제하시겠습니까?`)) return;
    try {
      await api.deleteNoticeAttachment(id, a.id);
      setExisting(prev => prev.filter(x => x.id !== a.id));
    } catch (e) { alert(e.message); }
  };

  const onZoneDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addPending(e.dataTransfer.files);
  };

  const save = async () => {
    setErr('');
    if (!title.trim()) { setErr('제목을 입력하세요.'); return; }
    setBusy(true);
    try {
      let postId = id;
      if (editing) {
        await api.patchPost(kind, id, { title, content_html: html });
      } else {
        const r = await api.createPost(kind, { title, content_html: html });
        postId = r.id;
      }
      if (isNotice && pending.length > 0) {
        const fd = new FormData();
        for (const f of pending) fd.append('files', f);
        await api.uploadNoticeAttachments(postId, fd);
      }
      nav('/' + kind + '/' + postId);
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <h1 className="text-xl font-bold text-medical-800 mb-3">{editing ? '글 수정' : '새 글 작성'}</h1>
      <input
        value={title} onChange={(e) => setTitle(e.target.value)} placeholder="제목"
        className="w-full mb-3 px-3 py-2 border border-medical-200 rounded-md text-lg"
      />
      <RichEditor value={html} onChange={setHtml} />

      {isNotice && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onZoneDrop}
          className={'mt-4 bg-white border rounded-lg p-4 transition ' +
            (dragOver ? 'border-medical-400 ring-2 ring-medical-200 bg-medical-50/40' : 'border-medical-100')}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-medical-800">첨부 파일</div>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-medical-50 hover:bg-medical-100 text-medical-700 text-sm cursor-pointer">
                📎 파일 추가
                <input
                  ref={fileInputRef} type="file" multiple className="hidden"
                  onChange={(e) => { addPending(e.target.files); e.target.value = ''; }}
                />
              </label>
              <span className="text-xs text-medical-500 break-keep">파일당 최대 200MB · 드래그&드롭 지원</span>
            </div>
          </div>
          {existing.length === 0 && pending.length === 0 && (
            <div className="text-xs text-medical-400 mt-1">첨부된 파일이 없습니다. 파일을 추가하면 공지 저장 시 함께 업로드됩니다.</div>
          )}
          <ul className="space-y-1 mt-1">
            {existing.map(a => (
              <li key={'e' + a.id} className="flex items-center gap-2 text-sm bg-medical-50/70 border border-medical-100 rounded px-2 py-1.5">
                <span className="text-medical-700 shrink-0">📄</span>
                <span className="text-medical-800 break-all flex-1">{a.original_name}</span>
                <span className="text-xs text-medical-500 shrink-0">{bytes(a.size_bytes)}</span>
                <span className="text-[11px] text-medical-500 break-keep hidden sm:inline shrink-0">{a.uploader_institution}</span>
                <button onClick={() => removeExisting(a)} className="text-red-500 hover:underline text-xs shrink-0">삭제</button>
              </li>
            ))}
            {pending.map((f, i) => (
              <li key={'p' + i} className="flex items-center gap-2 text-sm bg-amber-50/70 border border-amber-100 rounded px-2 py-1.5">
                <span className="text-amber-700 shrink-0">📥</span>
                <span className="text-medical-800 break-all flex-1">{f.name}</span>
                <span className="text-xs text-medical-500 shrink-0">{bytes(f.size)}</span>
                <span className="text-[11px] text-amber-700 shrink-0">저장 시 업로드</span>
                <button onClick={() => removePending(i)} className="text-red-500 hover:underline text-xs shrink-0">제거</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {err && <div className="text-sm text-red-600 mt-3">{err}</div>}

      <div className="mt-4 flex gap-2">
        <button
          onClick={save} disabled={busy}
          className="bg-medical-600 hover:bg-medical-700 disabled:opacity-60 text-white font-semibold px-4 py-2 rounded-md"
        >
          {busy ? '저장 중…' : '저장'}
        </button>
        <button
          onClick={() => nav(-1)} disabled={busy}
          className="bg-white border border-medical-200 text-medical-700 px-4 py-2 rounded-md disabled:opacity-60"
        >
          취소
        </button>
      </div>
    </div>
  );
}
